<template>
  <article
    :class="{
      'opacity-50': project.terminated_at
    }"
  >
    <h2 class="text-xl">
      {{ project.ref }}
      -
      <a
        class="text-primary-800 hover:text-primary-500 text-sm font-normal"
        :href="route('funding.projects.finance.index', linkData)"
      >
        Finance Breakdown
      </a>
    </h2>
    <table
      v-if="project.contracts.length"
      class="w-full border-collapse"
      cellspacing="0"
    >
      <thead>
        <th
          class="align-bottom text-left px-4 py-2 w-1/3 text-xs font-normal italic text-grey-700"
        >
          Click on the value of the claim to approve and reject it.
        </th>
        <th
          v-for="partner in partners"
          :key="partner.competition_project_partner_id"
          class="align-bottom text-left px-4 py-2 w-64 uppercase text-sm tracking-wide text-grey-700"
        >
          {{ partner.company.name.split(" ")[0] }}
        </th>
        <th
          class="align-bottom text-left px-4 py-2 w-64 uppercase text-sm tracking-wide text-primary-600"
        >
          Total
        </th>
      </thead>
      <tbody class="text-sm card is-borderless">
        <tr class="font-bold">
          <th
            class="px-4 py-2 text-left font-bold border-b-2 border-grey-900 bg-grey-800 text-white"
          >
            Total Grant
          </th>
          <td
            v-for="partner in partners"
            :key="partner.id"
            class="px-4 py-2 border-b-2 border-l border-grey-900 bg-grey-800 text-white"
          >
            <div class="flex justify-between">
              <strong>£</strong>
              <span class="text-right">{{ getFunding(partner.id) }}</span>
            </div>
          </td>
          <td
            class="px-4 py-2 border-b-2 border-l border-grey-900 bg-grey-800 text-white"
          >
            <div class="flex justify-between">
              <strong>£</strong>
              <span class="text-right">{{ projectGrant }}</span>
            </div>
          </td>
        </tr>
        <tr
          v-for="period in claimPeriods[project.competition_id]"
          :key="period + '_' + project.uuid"
        >
          <th class="px-4 py-2 border-b text-left font-normal">
            {{ splitClaimPeriod(formatMMYY(period)) }}
          </th>
          <funding-project-claim-value
            v-for="partner in partners"
            :key="partner.competition_project_partner_id"
            :project-uuid="project.uuid"
            :partner="partner"
            :claim-period="formatMMYY(period)"
            :claims="claimRequestsLookup"
            @claim:pay="payClaim"
            @claim:approve="approveClaim"
            @claim:reject="rejectClaim"
            @claim:bank="showBankDetails"
          />
          <td class="px-4 py-2 border-b border-l font-bold">
            <div class="flex justify-between">
              <strong>£</strong>
              <span class="text-right">{{
                periodTotal(formatMMYY(period))
              }}</span>
            </div>
          </td>
        </tr>
        <tr class="font-bold">
          <th
            class="px-4 py-2 text-left border-t-2 border-primary-200 bg-primary-100"
          >
            Remaining Grant
          </th>
          <td
            v-for="partner in partners"
            :key="partner.id"
            class="px-4 py-2 border-t-2 border-l border-primary-200 bg-primary-100"
          >
            <div class="flex justify-between">
              <strong>£</strong>
              <span class="text-right">{{
                getPartnerRemaningGrant(partner.id)
              }}</span>
            </div>
          </td>
          <td
            class="px-4 py-2 border-t-2 border-l border-primary-200 bg-primary-100"
          >
            <div class="flex justify-between">
              <strong>£</strong>
              <span class="text-right">{{ projectGrantRemaining }}</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <portal to="modals">
      <app-modal v-if="hasRejectionClaim">
        <app-card
          status="negative"
          class="w-full max-w-sm"
        >
          <h2 class="text-xl border-b px-6 py-4 mb-2">
            Reject claim?
          </h2>
          <div class="px-6 py-4">
            <label
              for="reason"
              class="font-bold uppercase tracking-wide block text-xs text-grey-400"
            >Reason</label>
            <textarea
              id="reason"
              ref="reason"
              v-model.trim="rejectionMessage"
              name="reason"
              class="block w-full h-24"
              :disabled="loading"
            />
          </div>
          <div class="flex px-6 py-4 border-t">
            <app-button
              colour="close"
              class="mr-4"
              @click="closeRejection"
            >
              Cancel
            </app-button>
            <app-button
              :disabled="loading || !rejectionMessage.length"
              @click="sendRejection"
            >
              Reject Claim
            </app-button>
          </div>
        </app-card>
      </app-modal>
    </portal>
    <portal to="modals">
      <app-modal
        v-if="showBankDetailsModal"
        @close="showBankDetailsModal = false"
      >
        <app-card class="w-full max-w-sm">
          <div class="p-6">
            <h1 class="text-lg pb-2 border-b mb-4">
              Bank Details for {{ bankDetails.company_name }}
            </h1>
            <h2 class="font-bold text-xs tracking-wide text-grey-600 uppercase">
              Bank Name
            </h2>
            <p>
              {{ bankDetails.bank_name }}
            </p>
            <h2
              class="font-bold text-xs tracking-wide text-grey-600 mt-4 uppercase"
            >
              Sort Code
            </h2>
            <p>
              {{ bankDetails.sort_code.substring(0, 2) }}-{{
                bankDetails.sort_code.substring(2, 4)
              }}-{{ bankDetails.sort_code.substring(4, 6) }}
            </p>
            <h2
              class="font-bold text-xs tracking-wide text-grey-600 mt-4 uppercase"
            >
              Account Name
            </h2>
            <p>
              {{ bankDetails.account_name || '---' }}
            </p>
            <h2
              class="font-bold text-xs tracking-wide text-grey-600 mt-4 uppercase"
            >
              Account Number
            </h2>
            <p>
              {{ bankDetails.account_number }}
            </p>
            <h2
              class="font-bold text-xs tracking-wide text-grey-600 mt-4 uppercase"
            >
              Address
            </h2>
            <p>
              {{ bankDetails.bank_address }}
            </p>
            <p class="mt-12">
              <app-button @click="showBankDetailsModal = false">
                Close
              </app-button>
            </p>
          </div>
        </app-card>
      </app-modal>
    </portal>
  </article>
</template>

<script>
import { parseISO, format } from "date-fns";
export default {
  name: "FundingProjectClaims",
  props: {
    fundingPot: {
      type: Number,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    claimPeriods: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    projectClaimRequests: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    projectBalances: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      loading: false,
      showRejectClaims: false,
      rejectionMessage: "",
      showBankDetailsModal: false,
      bankDetails: {},
      claimRequestsLookup: [],
      projectBalancesLookup: []
    };
  },
  computed: {
    hasRejectionClaim() {
      return this.claimRequestsLookup.find(
        request => request.id === this.showRejectClaims
      );
    },
    linkData() {
      return {
        funding: this.fundingPot,
        project: this.project.uuid
      };
    },
    contracts() {
      return this.project.contracts;
    },
    partners() {
      return this.project.partner_entries;
    },
    projectGrant() {
      const total = this.contracts.reduce(
        (total, contract) => total + contract.grant,
        0
      );
      return total && total > 0
        ? total.toLocaleString("en-GB", { minimumFractionDigits: 2 })
        : "---";
    },
    projectGrantRemaining() {
      let total = this.projectBalancesLookup.reduce(
        (total, balance) => total + balance.remaining,
        0
      );
      return total && total > 0
        ? total.toLocaleString("en-GB", { minimumFractionDigits: 2 })
        : "---";
    }
  },
  created() {
    this.claimRequestsLookup = this.projectClaimRequests.length
      ? this.projectClaimRequests
      : [];
    this.projectBalancesLookup = this.projectBalances.length
      ? this.projectBalances
      : [];
  },
  methods: {
    formatMMYY(date) {
      return format(parseISO(date), "MMyy");
    },
    splitClaimPeriod(date) {
      return date.substring(0, 2) + "/" + date.substring(2, 4);
    },
    getPartnerRemaningGrant(partner_id) {
      const balance = this.projectBalancesLookup.find(
        balance => balance.competition_project_partner_id === partner_id
      );
      return balance && balance.remaining > 0
        ? balance.remaining.toLocaleString("en-GB", {
            minimumFractionDigits: 2
          })
        : "---";
    },
    periodTotal(period) {
      const total = this.claimRequestsLookup
        .filter(
          request => request.claim_period === period && !request.rejected_at
        )
        .reduce((total, request) => total + request.value, 0);
      return total && total > 0
        ? total.toLocaleString("en-GB", { minimumFractionDigits: 2 })
        : "---";
    },
    getFunding(partner_id) {
      const contract = this.contracts.find(
        contract => contract.competition_project_partner_id === partner_id
      );
      return contract.grant
        ? contract.grant.toLocaleString("en-GB", { minimumFractionDigits: 2 })
        : "---";
    },
    hasSigned(partner_id) {
      const contract = this.contracts.find(
        contract => contract.competition_project_partner_id === partner_id
      );
      return contract.signed_by;
    },
    payClaim(id) {
      if (confirm("Are you sure you want to make this claim as paid?")) {
        return this.submitClaimStatus({
          claim_request_id: id,
          action: "pay"
        });
      }
    },
    approveClaim(id) {
      if (confirm("Are you sure you want to approve this claim?")) {
        return this.submitClaimStatus({
          claim_request_id: id,
          action: "approve"
        });
      }
    },
    rejectClaim(id) {
      this.showRejectClaims = id;
    },
    closeRejection() {
      this.showRejectClaims = false;
      this.rejectionMessage = "";
    },
    sendRejection() {
      return this.submitClaimStatus({
        claim_request_id: this.showRejectClaims,
        action: "reject",
        reason: this.rejectionMessage
      });
    },
    submitClaimStatus(payload) {
      this.loading = true;
      return axios
        .post(
          route("funding.claims.update-status", {
            funding: this.fundingPot,
            project: this.project.uuid
          }),
          payload
        )
        .then(({ data }) => this.updateLookup(data))
        .catch(() => alert("Unable to process request"))
        .then(() => (this.loading = false));
    },
    updateClaimRequestLookup(claim) {
      const claimIndex = this.claimRequestsLookup.findIndex(
        lookup => lookup.id === claim.id
      );
      return this.$set(this.claimRequestsLookup, claimIndex, claim);
    },
    updateProjectBalanceLookup(balance) {
      const claimIndex = this.projectBalancesLookup.findIndex(
        lookup => lookup.id === balance.id
      );
      return this.$set(this.projectBalancesLookup, claimIndex, balance);
    },
    updateLookup(data) {
      this.updateClaimRequestLookup(data.claim_request);
      this.updateProjectBalanceLookup(data.partner_balance);
      this.closeRejection();
    },
    showBankDetails(partner_id) {
      return axios
        .post(
          route("funding.claims.bank-details", {
            funding: this.fundingPot,
            project: this.project.uuid
          }),
          { partner_id }
        )
        .then(({ data }) => {
          this.bankDetails = data;
          this.showBankDetailsModal = partner_id;
        })
        .catch(() => alert("Unable to fetch partner bank details."));
    }
  }
};
</script>
